import { getStore } from '../store/configureStore';
import assets from './assets';
import { isApp } from './environment';

const baseUrl = isApp() ? getStore().getState().config.baseUrl : '';

const getSrc = imageSize => `${imageSize.src?.startsWith('/') ? baseUrl : ''}${imageSize.src}`;

export const getImageSize = (image, cropType, sizeSelector, options = {}) => {
  const { searchHighToLow, getLargestInstead } = options;

  if (!image?.crops) {
    return null;
  }

  const crop = image.crops.find(c => c.type === cropType);

  if (!crop?.sizes) {
    return null;
  }

  const sizesList = searchHighToLow ? [...crop.sizes].reverse() : [...crop.sizes];
  const foundImageSize = sizesList.find(sizeSelector);

  if (foundImageSize) {
    return { ...foundImageSize, src: getSrc(foundImageSize) };
  }

  if (getLargestInstead) {
    const largestSize = searchHighToLow ? sizesList[0] : sizesList[sizesList.length - 1];
    if (largestSize) {
      return { ...largestSize, src: getSrc(largestSize) };
    }
  }

  return null;
};

export const getImageSrc = (image, cropType, sizeSelector, options) =>
  getImageSize(image, cropType, sizeSelector, options)?.src;

export const getImageSrcSet = (image, cropType, selectionCriteria, options = {}) => {
  const { searchHighToLow } = options;
  let sizeFilter;
  let desiredSizes;
  if (typeof selectionCriteria === 'function') {
    sizeFilter = selectionCriteria;
  } else if (Array.isArray(selectionCriteria)) {
    desiredSizes = selectionCriteria;
  } else {
    throw new Error('Selection criteria must be a filter function or an array of desired sizes');
  }
  if (image?.crops) {
    const crop = image.crops.find(c => c.type === cropType);
    if (crop?.sizes) {
      if (sizeFilter) {
        return crop.sizes
          .filter(sizeFilter)
          .map(option => `${getSrc(option)} ${option.w}w`)
          .join(',');
      } else if (desiredSizes?.length) {
        const sizesList = searchHighToLow ? crop.sizes.slice().reverse() : crop.sizes;
        let cursor = 0;
        return desiredSizes
          .map(desiredSize => {
            const bestMatchIndex = sizesList.findIndex(
              size =>
                (desiredSize.w > 0 && size.w >= desiredSize.w) ||
                (desiredSize.h > 0 && size.h >= desiredSize.h),
            );
            const bestMatch = sizesList[bestMatchIndex] || sizesList[cursor + 1];
            if (bestMatchIndex) {
              cursor = bestMatchIndex;
            } else {
              cursor += 1;
            }
            if (bestMatch) {
              const label = desiredSize.x ? `${desiredSize.x}x` : `${bestMatch.w}w`;
              return `${getSrc(bestMatch)} ${label}`;
            }
            return null;
          })
          .filter(item => !!item)
          .join(',');
      }
    }
  }
  return null;
};

export const getCollectionCoverImage = (coverImageFrom, customCoverImage, options = {}) => {
  if (customCoverImage) {
    const customUrl = customCoverImage.startsWith('/')
      ? customCoverImage
      : `covers/${customCoverImage}`;
    return {
      source: assets.collectionUrl(customUrl, {
        resize: options.resize || 'fit-in',
        width: options.w || 1000,
        height: options.h || 1000,
        smart: true,
      }),
      width: options.w || 1000,
      height: options.h || 1000,
    };
  }
  if (coverImageFrom) {
    const primaryImage = coverImageFrom.primary_image;

    if (!primaryImage && !coverImageFrom.book.image_name) {
      return {
        source: 'http://static.ckbk.com/images/ckbk-logo.png',
        width: options.w || 240,
        height: options.h || 240,
      };
    } else if (!primaryImage) {
      return {
        source: assets.coverUrl(coverImageFrom.book.image_name, {
          resize: options.resizeBook || 'fit-in',
          width: options.w || 240,
          height: options.h || 240,
          background: options.background,
        }),
        width: options.w || 240,
        height: options.h || 240,
      };
    }
    const contentImage =
      getImageSize(
        primaryImage,
        options.crop || 'share',
        options.sizeSelector || (size => size.w <= 2000 && size.h <= 2000),
      ) ||
      getImageSize(primaryImage, options.crop || 'share', size => size.w <= 2000 && size.h <= 2000);
    return {
      source: `${baseUrl}${contentImage.src}`,
      width: contentImage.w,
      height: contentImage.h,
    };
  }

  return {
    source: assets.coverUrl('default', {
      resize: 'fit-in',
      width: 240,
      height: 240,
      background: options.background,
    }),
    width: 240,
    height: 240,
  };
};

export default { getImageSrc, getImageSize, getImageSrcSet, getCollectionCoverImage };
